import { useState } from 'react';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import { Bell, BellOff, BellRing, X } from 'lucide-react';
import { useNotifications } from '../hooks/useNotifications';

export function NotificationBanner() {
  const { supported, permission, enabled, requestPermission } = useNotifications();
  const [dismissed, setDismissed] = useState(false);

  // Don't show if not supported, already granted, or dismissed
  if (!supported || permission === 'granted' || permission === 'denied' || dismissed) {
    return null;
  }

  const handleEnable = async () => {
    await requestPermission();
  };

  return (
    <div className="bg-gradient-to-r from-emerald-500 to-teal-500 text-white px-4 py-3">
      <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
            <BellRing className="w-5 h-5" />
          </div>
          <div>
            <p className="font-medium">Activez les notifications</p>
            <p className="text-sm text-white/80">Recevez des alertes quand votre commande est prête</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            onClick={handleEnable}
            className="bg-white text-emerald-600 hover:bg-white/90 rounded-full"
          >
            Activer
          </Button>
          <button 
            onClick={() => setDismissed(true)}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

export function NotificationSettings() {
  const { supported, permission, enabled, requestPermission, toggleNotifications } = useNotifications();
  const [loading, setLoading] = useState(false);

  if (!supported) {
    return (
      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-gray-200 flex items-center justify-center">
            <BellOff className="w-5 h-5 text-gray-400" />
          </div>
          <div>
            <p className="font-medium text-gray-900">Notifications</p>
            <p className="text-sm text-gray-500">Non supportées par votre navigateur</p>
          </div>
        </div>
      </div>
    );
  }

  if (permission === 'denied') {
    return (
      <div className="flex items-center justify-between p-4 bg-red-50 rounded-xl">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
            <BellOff className="w-5 h-5 text-red-500" />
          </div>
          <div>
            <p className="font-medium text-gray-900">Notifications bloquées</p>
            <p className="text-sm text-gray-500">Modifiez les paramètres de votre navigateur pour les activer</p>
          </div>
        </div>
      </div>
    );
  }

  const handleToggle = async (checked) => {
    if (checked && permission !== 'granted') {
      setLoading(true);
      await requestPermission();
      setLoading(false);
    } else {
      toggleNotifications(checked);
    }
  };

  return (
    <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl">
      <div className="flex items-center gap-3">
        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${enabled ? 'bg-emerald-100' : 'bg-gray-200'}`}>
          {enabled ? (
            <Bell className="w-5 h-5 text-emerald-600" />
          ) : (
            <BellOff className="w-5 h-5 text-gray-400" />
          )}
        </div>
        <div>
          <p className="font-medium text-gray-900">Notifications push</p>
          <p className="text-sm text-gray-500">
            {enabled ? 'Vous recevrez des alertes pour vos commandes' : 'Activez pour recevoir des alertes'}
          </p>
        </div>
      </div>
      <Switch 
        checked={enabled} 
        onCheckedChange={handleToggle}
        disabled={loading}
        className="data-[state=checked]:bg-emerald-500"
      />
    </div>
  );
}

export function NotificationPrompt({ onClose }) {
  const { requestPermission } = useNotifications();
  const [loading, setLoading] = useState(false);

  const handleEnable = async () => {
    setLoading(true);
    await requestPermission();
    setLoading(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50">
      <div className="bg-white rounded-2xl p-6 max-w-sm w-full shadow-xl animate-scale-in">
        <div className="text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <BellRing className="w-8 h-8 text-emerald-600" />
          </div>
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Restez informé !
          </h3>
          <p className="text-gray-500 mb-6">
            Activez les notifications pour savoir quand votre commande est prête à être récupérée.
          </p>
          <div className="space-y-3">
            <Button 
              onClick={handleEnable}
              disabled={loading}
              className="w-full bg-emerald-500 hover:bg-emerald-600 rounded-full"
            >
              {loading ? 'Activation...' : 'Activer les notifications'}
            </Button>
            <Button 
              variant="ghost"
              onClick={onClose}
              className="w-full text-gray-500"
            >
              Plus tard
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
